import type { Expense } from "./types";

/** Remind the person to back up after this many entries without an export or Save project. */
export const BACKUP_REMINDER_EVERY = 20;

/** What the Expense Tracker keeps on this device when the person opts in. Receipt files are never part of it. */
export interface KeptExpenses {
  entries: Expense[];
  currency: string;
  budget: string;
  month: string;
  from: string;
  to: string;
  categorySet: string;
  /** Entry count at the last backup, so the reminder survives a reload. */
  backedUp: number;
}

/** Builds the snapshot. The receipt file name is dropped too: the file itself is not kept, so the name would point at nothing. */
export function keptExpenseSnapshot(s: { entries: Expense[]; currency: string; budget: string; month: string; from: string; to: string; categorySet: string; backedUp: number }): KeptExpenses {
  return { ...s, entries: s.entries.map(({ receipt: _receipt, ...rest }) => rest as Expense) };
}

const str = (v: unknown): string => (typeof v === "string" ? v.slice(0, 300) : "");

/** Accepts only a well-formed saved copy; unreadable entries are skipped rather than trusted. */
export function validateKeptExpenses(raw: unknown): KeptExpenses | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.entries)) return null;
  const entries = r.entries.filter((x): x is Expense => {
    const e = x as Partial<Expense> | null;
    return !!e && typeof e === "object" && typeof e.id === "string" && !!e.id && typeof e.date === "string" && typeof e.amount === "number" && Number.isFinite(e.amount) && (e.kind === "cost" || e.kind === "income") && typeof e.currency === "string";
  }).map(({ receipt: _receipt, ...rest }) => rest as Expense);
  if (entries.length === 0 && r.entries.length > 0) return null;
  return {
    entries, currency: str(r.currency) || "USD", budget: str(r.budget), month: str(r.month), from: str(r.from), to: str(r.to), categorySet: str(r.categorySet),
    backedUp: typeof r.backedUp === "number" && r.backedUp >= 0 ? Math.floor(r.backedUp) : 0,
  };
}
